import { collection, doc, runTransaction, serverTimestamp, setDoc, Timestamp, } from "firebase/firestore";
import { prepareForFirestore } from "./utils";
export const callConverter = {
    toFirestore(value) {
        const data = prepareForFirestore(value);
        if ("transcriptionWarning" in value) {
            data.transcriptionWarning = value.transcriptionWarning ?? null;
        }
        const transcriptFeedback = value.transcriptFeedback;
        if (transcriptFeedback?.submittedAt) {
            data.transcriptFeedback = {
                ...transcriptFeedback,
                submittedAt: transcriptFeedback.submittedAt instanceof Date
                    ? Timestamp.fromDate(transcriptFeedback.submittedAt)
                    : Timestamp.fromDate(new Date(transcriptFeedback.submittedAt)),
            };
        }
        const transcriptEdit = value.transcriptEdit;
        if (transcriptEdit?.editedAt) {
            data.transcriptEdit = {
                ...transcriptEdit,
                editedAt: transcriptEdit.editedAt instanceof Date
                    ? Timestamp.fromDate(transcriptEdit.editedAt)
                    : Timestamp.fromDate(new Date(transcriptEdit.editedAt)),
            };
        }
        return data;
    },
    fromFirestore(snapshot, options) {
        const data = snapshot.data(options);
        return {
            ...data,
            id: snapshot.id,
            transcriptionWarning: data.transcriptionWarning ?? null,
            transcriptFeedback: data.transcriptFeedback
                ? {
                    ...data.transcriptFeedback,
                    submittedAt: data.transcriptFeedback.submittedAt instanceof Timestamp
                        ? data.transcriptFeedback.submittedAt.toDate()
                        : data.transcriptFeedback.submittedAt,
                }
                : data.transcriptFeedback,
            transcriptEdit: data.transcriptEdit
                ? {
                    ...data.transcriptEdit,
                    editedAt: data.transcriptEdit.editedAt instanceof Timestamp
                        ? data.transcriptEdit.editedAt.toDate()
                        : data.transcriptEdit.editedAt,
                }
                : data.transcriptEdit,
            // Handle date conversions if needed, similar to actionItems
            createdAt: data.createdAt instanceof Timestamp
                ? data.createdAt.toDate()
                : data.createdAt
                    ? new Date(data.createdAt)
                    : null,
            updatedAt: data.updatedAt instanceof Timestamp
                ? data.updatedAt.toDate()
                : data.updatedAt
                    ? new Date(data.updatedAt)
                    : null,
        };
    },
};
export function callsCollection(db, companyId) {
    return collection(db, "companies", companyId, "calls").withConverter(callConverter);
}
export class TranscriptEditConflictError extends Error {
    constructor() {
        super("The transcript changed while it was being edited.");
        this.name = "TranscriptEditConflictError";
    }
}
export async function hashTranscriptText(text) {
    const bytes = new TextEncoder().encode(text);
    const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
export async function saveTranscriptEdit(db, companyId, callId, params) {
    const callRef = doc(db, "companies", companyId, "calls", callId);
    const revisionRef = doc(collection(callRef, "transcriptEdits"));
    const nextHash = await hashTranscriptText(params.text);
    await runTransaction(db, async (transaction) => {
        const snapshot = await transaction.get(callRef);
        if (!snapshot.exists())
            throw new Error("Call not found.");
        const call = snapshot.data();
        const currentText = typeof call.transcriptEdit?.text === "string"
            ? call.transcriptEdit.text
            : typeof call.formattedTranscript === "string"
                ? call.formattedTranscript
                : typeof call.transcript === "string"
                    ? call.transcript
                    : "";
        const currentHash = typeof call.transcriptEdit?.hash === "string"
            ? call.transcriptEdit.hash
            : await hashTranscriptText(currentText);
        if (currentHash !== params.expectedHash) {
            throw new TranscriptEditConflictError();
        }
        const editedAt = serverTimestamp();
        const edit = {
            text: params.text,
            hash: nextHash,
            revisionId: revisionRef.id,
            editedBy: params.editedBy,
            editedAt,
        };
        transaction.set(revisionRef, {
            ...edit,
            previousHash: currentHash,
        });
        transaction.update(callRef, {
            transcriptEdit: edit,
            updatedAt: editedAt,
        });
    });
}
export function callDocument(db, companyId, callId) {
    return doc(db, "companies", companyId, "calls", callId).withConverter(callConverter);
}
export async function updateCall(db, companyId, callId, data) {
    const docRef = callDocument(db, companyId, callId);
    await setDoc(docRef, {
        ...data,
        updatedAt: new Date(),
    }, { merge: true });
}
